// contexts/BasketSummaryContext.tsx
import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import axios, { AxiosResponse } from 'axios';
import { retrieveProductsSummary } from '../../services/product.services';

interface BasketSummary {
  products: Item[];
  subtotal: number;
  dueNow: number;
  recurringCharges: number;
  estimatedVAT: number;
  needsMCA?: boolean;
  needsAdditionalInfo?: boolean;
  needsAdditionalInformation?: boolean;
}

interface BasketSummaryContextType {
  basketSummary: AxiosResponse<BasketSummary, any> | null;
  fetchBasketSummary: (userBasket: any, user: any) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const BasketSummaryContext = createContext<BasketSummaryContextType | undefined>(undefined);

export const BasketSummaryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [basketSummary, setBasketSummary] = useState<AxiosResponse<BasketSummary, any> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBasketSummary = async (userBasket: any, user: any) => {
    if (!userBasket?.items || userBasket.items.length === 0) {
      setBasketSummary(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const sessionPrices = sessionStorage.getItem("originalBasketPrices")
    console.log('sessionPrices', sessionPrices);

    try {
      const parsedSessionPrices = sessionPrices ? JSON.parse(sessionPrices) : {};
      const productsList = userBasket.items.map((item: any) => ({
        id: item.productId,
        price: parsedSessionPrices[item.productId] || item.dxlInformation?.price?.[0]?.price || 0,
        numberOfUnits: item.quantity || 0,
        pricingType: item.dxlInformation?.pricingType || 'MONTHLY',
      }));

      const response = await retrieveProductsSummary(productsList, user?.csrfToken || '');

      if (!axios.isAxiosError(response)) {
        setBasketSummary(response);

      } else {
        setError('Error fetching basket summary');
        console.error('Error fetching summary:', response);
      }
    } catch (err) {
      setError('Failed to fetch basket summary');
      console.error('Error fetching summary:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue = useMemo(
    () => ({
      basketSummary,
      fetchBasketSummary,
      setBasketSummary,
      isLoading,
      error,
    }),
    [basketSummary, isLoading, error]
  );

  return <BasketSummaryContext.Provider value={contextValue}>{children}</BasketSummaryContext.Provider>;
};

export const useBasketSummary = () => {
  const context = useContext(BasketSummaryContext);
  if (context === undefined) {
    throw new Error('useBasketSummary must be used within a BasketSummaryProvider');
  }
  return context;
};
